/**
 * Profile photo: shows the current photo (or initials) with Change / Remove.
 * The image is resized in the browser to ≤ 512px JPEG before upload, then
 * POST /api/me/avatar stores it and the signed-in user is refreshed.
 */
import { useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

async function resizeToJpeg(file: File, max = 512): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  // Center square crop (fills the round avatar), scaled down to at most `max`.
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = Math.min(max, side);
  canvas.getContext('2d')!.drawImage(
    bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, canvas.width, canvas.height
  );
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode_failed'))), 'image/jpeg', 0.85)
  );
}

export function AvatarUploader() {
  const { user, refresh } = useAuth();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const name = user?.display_name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '?';
  const initial = name.trim()[0]?.toUpperCase() ?? '?';

  async function onPick(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await resizeToJpeg(file);
      await api.upload('/api/me/avatar', blob);
      await refresh();
    } catch {
      setError("Couldn't upload that photo. Try a different image.");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.del('/api/me/avatar');
      await refresh();
    } catch {
      setError("Couldn't remove the photo.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card flex items-center gap-4">
      <button
        type="button"
        onClick={() => input.current?.click()}
        disabled={busy}
        className="relative w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold overflow-hidden flex-shrink-0"
        style={{ background: 'var(--gold)', color: 'var(--navy)' }}
        aria-label="Change photo"
      >
        {user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" /> : initial}
        <span className="absolute bottom-0 right-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'var(--ink)', color: 'var(--champagne)' }}>
          <Camera size={13} />
        </span>
      </button>
      <div className="flex flex-col gap-1.5">
        <p className="text-white font-semibold">Profile photo</p>
        <div className="flex gap-3 text-sm">
          <button type="button" onClick={() => input.current?.click()} disabled={busy} style={{ color: 'var(--champagne)' }}>
            {busy ? 'Saving…' : user?.avatar_url ? 'Change photo' : 'Add photo'}
          </button>
          {user?.avatar_url && !busy && (
            <button type="button" onClick={remove} style={{ color: 'var(--gray-400)' }}>Remove</button>
          )}
        </div>
        {error && <p className="text-xs" style={{ color: 'var(--error)' }}>{error}</p>}
      </div>
      <input ref={input} type="file" accept="image/*" hidden onChange={(e) => onPick(e.target.files?.[0])} />
    </div>
  );
}
